import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import useLocalStorageState, {
  LocalStorageState,
} from "use-local-storage-state";

import { createStorageKey } from "@/utils/storage";

const THEMES = ["dark", "light", "system"] as const;

export type Theme = (typeof THEMES)[number];
export type ResolvedTheme = "dark" | "light";

export function isTheme(value: unknown): value is Theme {
  return THEMES.includes(value as Theme);
}

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

const ThemeContext = createContext<{
  theme: Theme;
  setTheme: LocalStorageState<Theme>[1];
  resolvedTheme: ResolvedTheme;
  isDarkMode: boolean;
} | null>(null);

function subscribeToSystemColorScheme(onChange: () => void) {
  const query = window.matchMedia(COLOR_SCHEME_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function isSystemDarkMode() {
  return window.matchMedia(COLOR_SCHEME_QUERY).matches;
}

function toggleDarkClassWithoutTransitions(isDark: boolean) {
  const style = document.createElement("style");
  style.textContent =
    "*,*::before,*::after{-webkit-transition:none!important;transition:none!important}";
  document.head.appendChild(style);

  document.documentElement.classList.toggle("dark", isDark);

  // Force a reflow so the class change paints before transitions come back.
  window.getComputedStyle(document.body);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => style.remove());
  });
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [storedTheme, setTheme] = useLocalStorageState<Theme>(
    createStorageKey("theme"),
    { defaultValue: "system" },
  );
  // localStorage is user-writable, so the stored value is not guaranteed a Theme.
  const theme = isTheme(storedTheme) ? storedTheme : "system";

  const systemPrefersDark = useSyncExternalStore(
    subscribeToSystemColorScheme,
    isSystemDarkMode,
  );
  const resolvedTheme: ResolvedTheme =
    theme === "system" ? (systemPrefersDark ? "dark" : "light") : theme;
  const isDarkMode = resolvedTheme === "dark";

  useEffect(() => {
    toggleDarkClassWithoutTransitions(isDarkMode);
  }, [isDarkMode]);

  const context = useMemo(
    () => ({ theme, setTheme, resolvedTheme, isDarkMode }),
    [theme, setTheme, resolvedTheme, isDarkMode],
  );

  return (
    <ThemeContext.Provider value={context}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export function useColorModeValue<T>(lightValue: T, darkValue: T) {
  const { isDarkMode } = useTheme();
  return isDarkMode ? darkValue : lightValue;
}
